/* eslint-disable camelcase */
import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';

import { useHistory } from 'react-router';
import { useTranslation } from 'react-i18next';
import Card from '@material-ui/core/Card';
import Grid from '@material-ui/core/Grid';

import { GenericForm, ConfirmationModal } from '@components';
import Button from '@components/Forms/Button';
import InputText from '@components/Inputs/InputText';
import Select from '@components/Forms/Select';
import { useBankAccount, useCommon } from '@hooks';
import { capitalize, substractOptionArray } from '@helpers';
import { extractCciFromBankAccount } from '@helpers/bankAccount';
import { bankAccountTypePeru, saparateDocSelected } from '@constants/bankAccountType';
import styled from 'styled-components';

import classes from './FormBankAccount.module.scss';
import schemaPe from './schemaPe';

const TextNote = styled.h4`
  font-family: Roboto;
  font-size: 14px;
  line-height: 16px;
  padding: 16px;
  border-radius: 6px;
  background: #c0dbf3;
  color: #0a5aa2 !important;
`;

const FormBankAccountPe = ({ edit, docs }) => {
  const { t } = useTranslation();
  const history = useHistory();
  const { setModal } = useCommon();
  const {
    bankAccount: initialBankAccount,
    createBankAccount,
    updateBankAccount,
    banksCodes,
  } = useBankAccount();
  const [bankAccount, setBankAccount] = useState(edit ? initialBankAccount : {});
  const [anyChange, setChange] = useState(false);
  const [cci, setCCI] = useState(edit ? extractCciFromBankAccount(initialBankAccount) : '');
  const [docSelected, setDocSelected] = useState(
    edit && initialBankAccount?.owner_data
      ? `${initialBankAccount.owner_data?.doc_type}${saparateDocSelected}${initialBankAccount.owner_data?.doc_number}`
      : ''
  );

  const handleOwnerDataChange = (e) => {
    setChange(true);
    if (e.target.name === 'docSelected') {
      setDocSelected(e.target.value);
      setBankAccount({
        ...bankAccount,
        owner_data: {
          ...bankAccount.owner_data,
          doc_number: e.target.value.split(saparateDocSelected)[1],
          doc_type: e.target.value.split(saparateDocSelected)[0],
        },
      });
    } else {
      setBankAccount({
        ...bankAccount,
        owner_data: {
          ...bankAccount.owner_data,
          [e.target.name]: e.target.value,
        },
      });
    }
  };

  const handleAccountDataChange = (e) => {
    setChange(true);
    setBankAccount({
      ...bankAccount,
      account_data: {
        ...bankAccount.account_data,
        [e.target.name]: e.target.value,
      },
    });
  };

  const handleBankChange = (e) => {
    const bankSelected = Object.values(banksCodes).find((item) => item.code === e.target.value);

    setChange(true);
    setBankAccount({
      ...bankAccount,
      bank_data: {
        finantial_institution_name: bankSelected.bank,
        finantial_institution_id: bankSelected.code,
      },
    });
    setCCI(bankSelected.code);
  };

  const handleCancel = () => {
    history.goBack();
  };

  useEffect(() => {
    if (cci?.length === 20)
      setBankAccount({
        ...bankAccount,
        account_data: {
          ...bankAccount.account_data,
          product_code: cci?.slice(6, 8),
          sub_product_code: '9999',
          currency_code: cci?.slice(8, 10),
          account_number_CCI: cci?.slice(10, 18),
        },
        bank_data: {
          ...bankAccount.bank_data,
          finantial_institution_identifier: cci?.slice(17, 18),
          finantial_institution_office: cci?.slice(3, 6),
          cci_verification_code: cci?.slice(-2),
        },
      });
    // eslint-disable-next-line
  }, [cci]);

  const handleSubmit = () => {
    if (edit) {
      setModal({
        body: (
          <ConfirmationModal
            title={t('bankAccount.editBankAccount.modalConfirm.title')}
            handleConfirm={() => {
              updateBankAccount(bankAccount);
            }}
          />
        ),
      });
    } else {
      createBankAccount(bankAccount);
    }
  };

  const setButtonEnabled =
    !cci ||
    !docSelected ||
    !bankAccount.account_data?.account_type_code ||
    !bankAccount.account_data?.account_number;

  return (
    <>
      {
        /** TODO: motivo de requerir docs no vacio
         * En edicion el campo tiene valor previamente cargado y no machea con opciones en @param props.docs
         * props.docs inicialmente es un array vacio por lo que provoca que se rompa el <Select /> de material-ui.
         */
        docs.length > 0 && (
          <Card className={['popup_card', classes.card].join(' ')}>
            {edit ? (
              <h3>{t('bankAccount.editBankAccount.subtitle')}</h3>
            ) : (
              <h3>{t('paymentsFoundsOut.common.bankAccount')}</h3>
            )}
            <TextNote>{t('paymentsFoundsOut.common.description')}</TextNote>
            <GenericForm
              data-testid="formbankaccount-form"
              justify="flex-end"
              schema={schemaPe}
              item
              xs={12}
              onSubmit={handleSubmit}
              container
              spacing={3}
            >
              <Select
                fullWidth
                label={t('paymentsFoundsOut.common.document')}
                value={docSelected}
                id="doc_number"
                placeholder={capitalize('paymentsFoundsOut.common.document')}
                className={classes.select}
                variant="outlined"
                handleMyChange={handleOwnerDataChange}
                options={docs}
                gridProps={{ xs: 12, item: true, className: classes.gridSelect }}
                name="docSelected"
              />
              <Select
                fullWidth
                handleMyChange={handleBankChange}
                id="finantial_institution_name"
                label="Banco"
                value={bankAccount?.bank_data?.finantial_institution_id}
                variant="outlined"
                className={classes.select}
                options={substractOptionArray(Object.values(banksCodes), 'code', 'bank')}
                gridProps={{ xs: 12, item: true, className: classes.gridSelect }}
              />
              <InputText
                label={t('paymentsFoundsOut.common.accountNumber')}
                handleChange={(e) => {
                  setChange(true);
                  setCCI(e.target.value);
                }}
                value={cci}
                id="interbank_account_code"
                className={classes.input}
                variant="outlined"
                gridProps={{ xs: 12, item: true, className: classes.gridInput }}
                inputProps={{
                  maxLength: 20,
                }}
              />
              <InputText
                label={t('bankAccount.bankNumber')}
                handleChange={handleAccountDataChange}
                value={bankAccount.account_data?.account_number}
                id="account_number"
                name="account_number"
                className={classes.input}
                variant="outlined"
                gridProps={{ xs: 12, item: true, className: classes.gridInput }}
                inputProps={{
                  minLength: 12,
                  maxLength: 18,
                  title: '',
                }}
              />
              <Select
                fullWidth
                label={t('bankAccount.bankType')}
                value={bankAccount.account_data?.account_type_code}
                id="account_type_code"
                placeholder={capitalize('paymentsFoundsOut.common.document')}
                className={classes.select}
                variant="outlined"
                handleMyChange={handleAccountDataChange}
                options={bankAccountTypePeru}
                name="account_type_code"
                gridProps={{ xs: 12, item: true, className: classes.gridSelect }}
              />
              {edit ? (
                <Grid item xs={12} className={classes.formButtonContainer} gridProps={{ xs: 12 }}>
                  <Button
                    id="cancel-edit-bank-account"
                    onClick={handleCancel}
                    variant="outlined"
                    data-testid="formbankaccount-cancel-button"
                  >
                    {t('bankAccount.editBankAccount.cancel')}
                  </Button>
                  <Button
                    id="confirm-edit-bank-account"
                    type="submit"
                    data-testid="formbankaccount-save-button"
                    disabled={!anyChange || setButtonEnabled}
                  >
                    {t('bankAccount.editBankAccount.save')}
                  </Button>
                </Grid>
              ) : (
                <Grid item xs={12} className={classes.formButtonContainer} gridProps={{ xs: 12 }}>
                  <Button
                    onClick={handleCancel}
                    id="cancel-bank-account"
                    variant="outlined"
                    data-testid="formbankaccount-cancel-button"
                  >
                    {t('bankAccount.editBankAccount.cancel')}
                  </Button>
                  <Button
                    cardButton
                    id="confirm-bank-account"
                    type="submit"
                    data-testid="formbankaccount-add-button"
                    disabled={setButtonEnabled}
                  >
                    {t('paymentsFoundsOut.common.add')}
                  </Button>
                </Grid>
              )}
            </GenericForm>
          </Card>
        )
      }
    </>
  );
};

FormBankAccountPe.propTypes = {
  edit: PropTypes.bool,
  docs: PropTypes.array.isRequired,
};

FormBankAccountPe.defaultProps = {
  edit: false,
};

export default FormBankAccountPe;
